import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, HostBinding, Inject, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MainMenuElement } from '@m4u-angular/base/classes/BaseClasses';
import { AppConfigService } from '@m4u-angular/configuration/app.config.service';
import { BottomSheetService } from '@m4u-angular/core/framework/bottom-sheet.service';
import { ScreenService } from '@m4u-angular/core/framework/screen.service';
import { SessionService, SessionStates } from '@m4u-angular/core/framework/session.service';
import { SidebarService } from '@m4u-angular/core/framework/sidebar.service';
import { SysEventsService } from '@m4u-angular/core/framework/sysevents.service';
import { combineLatest, filter, map, mergeMap, Subscription, take, takeWhile, tap } from 'rxjs';
import { LanguageService } from '@m4u-angular/core/framework/language.service';
import { ShareService } from '@m4u-angular/core/framework/share.service';

@Component({
    selector: 'm4u-app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

    @HostBinding('class') get getClass() {
        const rtlClass = this.screen.IsRTL() ? ' RTL' : '';
        const classList = `${Object.keys(this.screen.Sizes).filter(cl => this.screen.Sizes[cl]).join(' ')}${rtlClass}`;
        return classList;
    }
    @ViewChild('sidebarContainer', { read: ViewContainerRef }) containerRef: ViewContainerRef;
    @ViewChild('bottomSheetContainer', { read: ViewContainerRef }) bottomSheetContainerRef: ViewContainerRef;

    shouldGetInitialURL = true;
    initialURL: string;
    loadPanelVisible = false;
    isLoading = false;

    private _subscriptions: Subscription[] = [];
    private menuItems: MainMenuElement[];

    constructor(
        @Inject(DOCUMENT) private document,
        @Inject(APP_BASE_HREF) private baseUrl: string,
        public screen: ScreenService,
        private router: Router,
        private events: SysEventsService,
        private sessionService: SessionService,
        private appConfigService: AppConfigService,
        private sidebarService: SidebarService,
        private shareService: ShareService,
        private activatedRoute: ActivatedRoute,
        private bottomSheetService: BottomSheetService,
    ) {
        //bottomSheet: BottomSheetProvider,
        // vcRef: ViewContainerRef) {
        //bottomSheet.rootVcRef = vcRef;
    }

    ngOnInit() {
        // get initial url to select ccertain menu item.
        // cannot do it inside main cycle of handling routes cuz angular.ROUTES object raises this event earlier
        // that we make possible to handle it with all necessary data
        this.getInitialRoute();

        this._subscriptions.push(this.sessionService.sessionStateChanged$.subscribe(state => {
            if (state === SessionStates.Started) {
                this.handleRoutes();
            }
        }));
        this._subscriptions.push(this.events.LoadPanel.subscribe(visible => {
            this.loadPanelVisible = visible;
            if (visible) {
                setTimeout(() => {
                    if (this.loadPanelVisible) this.loadPanelVisible = false;
                }, 3000);
            }
        }));
        this._subscriptions.push(this.events.Loading$.subscribe(visible => {
            this.isLoading = visible;
            if (visible) {
                setTimeout(() => {
                    if (this.isLoading) this.isLoading = false;
                }, 10000);
            }
        }));

        if (this.baseUrl) {
            const baseElements = this.document.getElementsByTagName('base');

            if (baseElements.length > 0) {
                baseElements[0].setAttribute('href', this.baseUrl);
            }
        }
    }

    ngAfterViewInit(): void {
        this.sidebarService.ViewContainerRef = this.containerRef;
        this.shareService.ViewContainerRef = this.containerRef;
        this.bottomSheetService.ViewContainerRef = this.bottomSheetContainerRef;
    }

    getSelectedMenuItem(menuItems: MainMenuElement[], selectedRoute: string): MainMenuElement {
        let selectedMenuItem;
        menuItems.forEach(menuItem => {
            if (menuItem.path === selectedRoute) {
                selectedMenuItem = menuItem;
                return;
            }
            if (!selectedMenuItem && menuItem.items) selectedMenuItem = this.getSelectedMenuItem(menuItem.items, selectedRoute);
        });
        return selectedMenuItem;
    }

    ngOnDestroy() {
        this._subscriptions.forEach(x => x.unsubscribe());
    }

    handleNavigationEnd(selectedRoute: string) {
        const selectedMenuItem = this.getSelectedMenuItem(this.menuItems, selectedRoute);
        this.events.ChangeSelectedRoute(selectedMenuItem && selectedMenuItem.menu);
    }

    getInitialRoute() {
        // getting 
        this.router.events
            .pipe(
                takeWhile(x => this.shouldGetInitialURL),
                filter(event => event instanceof NavigationEnd))
            .subscribe(event => {
                if (this.shouldGetInitialURL) {
                    if (this.menuItems) {
                        this.shouldGetInitialURL = false;
                    } else {
                        this.initialURL = this.GetCurrentUrl();
                    }
                }
            });
    }

    handleRoutes() {
        // Only after session starts get menu items
        this._subscriptions.push(
            combineLatest([this.appConfigService.AppConfig.OnGetMenu(this.sessionService.UserInfoDict), 
                this.appConfigService.AppConfig.OnGetNavbarItems(this.sessionService.UserInfoDict)])
                .pipe(
                    map(([menuItems, navbarItems]) => [...menuItems, ...navbarItems]),
                    take(1),
                    tap(x => {
                        this.menuItems = x;
                        if (this.initialURL) {
                            this.shouldGetInitialURL = false;
                            this.handleNavigationEnd(this.initialURL);
                            this.initialURL = null;
                        }
                    }),
                    mergeMap(() => this.router.events),
                    filter(event => event instanceof NavigationEnd))
                .subscribe(event => {
                    this.shouldGetInitialURL = false;
                    this.handleNavigationEnd(this.GetCurrentUrl());
                })
        );
    }

    private GetCurrentUrl() {
        let route = this.activatedRoute;
        let urlSegments = route.snapshot.url;
        // get the bottom route and url by all the tree
        while (route.firstChild) {
            route = route.firstChild;
            urlSegments = [...urlSegments, ...route.snapshot.url];
        }
        if (route.snapshot.params && route.snapshot.params.EntryID && !route.snapshot.data.IsExternal) {
            const entryIdUrlSegment = urlSegments.find(x => x.path === route.snapshot.params.EntryID);
            entryIdUrlSegment && (urlSegments = urlSegments.filter(x => x !== entryIdUrlSegment));
        }
        const url = urlSegments.map(x => x.path).join('/');
        return `/${url.substring(0, url.indexOf('_') - 1)}${url.substring(url.lastIndexOf('_') + 1)}`;
    }
}
